import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaveBar } from "../../utils/SaveBar";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import ModernEmployeeSelector from "../../utils/Selector/ModernEmployeeSelector";
import RoleOnly, { useUserHasAccess } from "../../../routes/roleOnly";
import {
  getChemicalInventorySettings,
  updateChemicalInventorySettings,
} from "../../../api/hse/chemicalInventorySettings";
import {
  normalizeChemicalInventorySettings,
  snapshotChemicalInventorySettings,
} from "../../../utils/chemicalInventoryAccess";

const EMPTY_SETTINGS = normalizeChemicalInventorySettings({});

export const ChemicalInventorySettingsForm = () => {
  const { t } = useTranslation();
  const canSave = useUserHasAccess({ role: "admin" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [savedSnapshot, setSavedSnapshot] = useState(
    snapshotChemicalInventorySettings(EMPTY_SETTINGS)
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const applyLoaded = (raw) => {
    const next = normalizeChemicalInventorySettings(raw);
    setSettings(next);
    setSavedSnapshot(snapshotChemicalInventorySettings(next));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const raw = await getChemicalInventorySettings();
        if (!cancelled) applyLoaded(raw);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasChanges = useMemo(
    () => snapshotChemicalInventorySettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patch = (partial) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...partial }));
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(false);
    setSaveSuccess(false);
    try {
      const raw = await updateChemicalInventorySettings({
        employee_visibility: settings.employee_visibility,
        responsible_user_ids: settings.responsible_user_ids,
        default_review_interval_months: settings.default_review_interval_months,
        require_sds: settings.require_sds,
        auto_create_from_products: settings.auto_create_from_products,
        review_reminder_enabled: settings.review_reminder_enabled,
        sds_stale_after_months: settings.sds_stale_after_months,
      });
      applyLoaded(raw);
      setSaveSuccess(true);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading size="base" />;
  if (error) {
    return (
      <ResourceError errors={[t("chemical.inventory.settings.load_error")]} />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900">
          {t("chemical.inventory.settings.title")}
        </h3>
        <p className="text-sm text-blue-700 mt-1">
          {t("chemical.inventory.settings.intro")}
        </p>
      </div>

      <fieldset disabled={saving} className="min-w-0 bg-white shadow sm:rounded-lg p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("chemical.inventory.settings.visibility")}
          </label>
          <select
            value={settings.employee_visibility}
            onChange={(e) => patch({ employee_visibility: e.target.value })}
            disabled={!canSave}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">{t("chemical.inventory.settings.visibility_choose")}</option>
            <option value="all_active">
              {t("chemical.inventory.settings.visibility_all_active")}
            </option>
            <option value="hse_roles_only">
              {t("chemical.inventory.settings.visibility_hse_roles_only")}
            </option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("chemical.inventory.settings.responsible")}
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {(settings.responsible_users || []).map((user) => (
              <span
                key={user.id}
                className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800"
              >
                {user.displayTitle || user.name}
              </span>
            ))}
            {(settings.responsible_users || []).length === 0 ? (
              <span className="text-sm text-gray-500">
                {t("chemical.inventory.settings.responsible_empty")}
              </span>
            ) : null}
          </div>
          {canSave ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="mt-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("chemical.inventory.settings.choose_responsible")}
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("chemical.inventory.settings.review_interval")}
            </label>
            <input
              type="number"
              min="1"
              max="120"
              value={settings.default_review_interval_months}
              onChange={(e) =>
                patch({
                  default_review_interval_months: Number(e.target.value),
                })
              }
              disabled={!canSave}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("chemical.inventory.settings.sds_stale")}
            </label>
            <input
              type="number"
              min="1"
              max="120"
              value={settings.sds_stale_after_months}
              onChange={(e) =>
                patch({ sds_stale_after_months: Number(e.target.value) })
              }
              disabled={!canSave}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="space-y-3">
          {[
            ["require_sds", "chemical.inventory.settings.require_sds"],
            [
              "auto_create_from_products",
              "chemical.inventory.settings.auto_create",
            ],
            [
              "review_reminder_enabled",
              "chemical.inventory.settings.review_reminder",
            ],
          ].map(([field, key]) => (
            <label key={field} className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={Boolean(settings[field])}
                onChange={(e) => patch({ [field]: e.target.checked })}
                disabled={!canSave}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t(key)}
            </label>
          ))}
        </div>
      </fieldset>

      <RoleOnly role="admin">
        <SaveBar
          hasChanges={hasChanges}
          isSaving={saving}
          isSuccess={saveSuccess}
          isError={saveError}
          onSave={handleSave}
          onDismiss={() => setSaveError(false)}
          successMessage={t("chemical.inventory.settings.saved")}
          errorMessage={t("chemical.inventory.settings.save_error")}
          saveLabel={t("Save")}
        />
      </RoleOnly>

      <ModernEmployeeSelector
        open={pickerOpen}
        multiple
        useDialog
        selectedEmployees={settings.responsible_users || []}
        onConfirm={(employees) => {
          const list = Array.isArray(employees) ? employees : [];
          patch({
            responsible_users: list,
            responsible_user_ids: list.map((u) => u.id),
          });
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
};
